import { z } from 'zod';
import { EXPORT_FORMATS } from '../types/study';
import { EVENT_TYPES, SRS_STATES, STUDY_MODES, WORD_STATUSES } from '../types/index';
import { booleanQuery } from './content';
import { pageQuerySchema } from './common';

/**
 * 阶段 3–7 的请求校验：词典 / 生词本 / 复习 / 长难句分析 / 水平评估 / 学习建议 / 后台 / 埋点。
 *
 * 与 `content.ts` 相同的三条约定（中文 message、query 布尔显式解析、上限来自常量表），
 * 这里不再重复解释。额外补充一条**贯穿本文件的取舍**：
 *
 * 凡是"AI 可能失败"的入参都做成**可选**，把"能不能算"留给服务层判断。
 * 例如长难句分析既接受 `sentenceId`，也接受 `{articleId, globalIndex}`，还接受裸 `text`——
 * 前两者能复用缓存、能落库；第三者用于"用户从别处粘一句话来问"，
 * 无法落库但也不该报错。用 zod 的 `refine` 保证"至少给一种"。
 */

// ---------------------------------------------------------------------------
// 词典（阶段 3）
// ---------------------------------------------------------------------------

export const LOOKUP_LIMITS = {
  /** 单词/短语最大长度：超过的一律按"不是词"处理 */
  maxQueryChars: 64,
  /** 上下文片段上限（用于文中义消歧） */
  maxContextChars: 400,
  /** 超过这个长度的 query 视为句子而不是词，直接判为不合法 */
  maxPhraseWords: 6,
} as const;

export const dictionaryLookupSchema = z.object({
  /** 单词或短语。二选一与 `text` 互斥（服务层只认这个字段） */
  word: z.string().trim().min(1, '请输入要查询的单词').max(LOOKUP_LIMITS.maxQueryChars, '查询内容过长').optional(),
  /** 上下文原文（句子）：给了才会算"文中义" */
  context: z.string().trim().max(LOOKUP_LIMITS.maxContextChars, '上下文过长').optional(),
  articleId: z.string().trim().max(64).optional(),
  sentenceId: z.string().trim().max(64).optional(),
  /** 句内词序，用于把文中义写回 `article_word_senses`（同一 token 只算一次） */
  tokenIndex: z.coerce.number().int().min(0).max(1000).optional(),
  /** 是否顺带累加生词本的 lookup_count（阅读页点词时为 true） */
  track: z.boolean().optional(),
});
export type DictionaryLookupInput = z.infer<typeof dictionaryLookupSchema>;

// ---------------------------------------------------------------------------
// 生词本（阶段 4）
// ---------------------------------------------------------------------------

export const WORD_RECORD_LIMITS = {
  maxSurfaceChars: 64,
  maxNoteChars: 500,
  maxTags: 8,
  maxTagChars: 20,
  maxContextChars: 400,
} as const;

/**
 * 新增/更新一条生词记录。
 *
 * `wordId` 与 `surface` 二选一：查词卡片手里有 lemma 就传 `wordId`；
 * 手动加词（"我自己想背这个词"）只有拼写就传 `surface`，
 * 服务层会用 `lemmaOf(surface)` 归一化成 key。
 */
export const wordRecordCreateSchema = z
  .object({
    wordId: z.string().trim().min(1).max(64).optional(),
    surface: z.string().trim().min(1, '请输入单词').max(WORD_RECORD_LIMITS.maxSurfaceChars).optional(),
    status: z.enum(WORD_STATUSES).optional(),
    articleId: z.string().trim().max(64).optional(),
    /** 遇见这个词的原句，复习时展示 */
    contextSnippet: z.string().trim().max(WORD_RECORD_LIMITS.maxContextChars).optional(),
    note: z.string().trim().max(WORD_RECORD_LIMITS.maxNoteChars, '笔记过长').optional(),
    tags: z.array(z.string().trim().min(1).max(WORD_RECORD_LIMITS.maxTagChars)).max(WORD_RECORD_LIMITS.maxTags, '标签最多 8 个').optional(),
    /** 是否累加 lookup_count（点词收藏时为 true，手动加词为 false） */
    countLookup: z.boolean().optional(),
  })
  .refine((value) => Boolean(value.wordId ?? value.surface), {
    message: '请提供要加入生词本的单词',
    path: ['surface'],
  });
export type WordRecordCreateInput = z.infer<typeof wordRecordCreateSchema>;

export const wordRecordUpdateSchema = z.object({
  status: z.enum(WORD_STATUSES).optional(),
  note: z.string().trim().max(WORD_RECORD_LIMITS.maxNoteChars, '笔记过长').nullable().optional(),
  tags: z.array(z.string().trim().min(1).max(WORD_RECORD_LIMITS.maxTagChars)).max(WORD_RECORD_LIMITS.maxTags).optional(),
  /** 手动拖动熟悉度（0–100），用于"我已经会了" */
  familiarity: z.coerce.number().int().min(0).max(100).optional(),
});
export type WordRecordUpdateInput = z.infer<typeof wordRecordUpdateSchema>;

export const wordbookQuerySchema = pageQuerySchema.extend({
  status: z.enum(WORD_STATUSES).optional(),
  /** 只看今天到期的 */
  due: booleanQuery,
  /** 模糊搜索拼写 */
  q: z.string().trim().max(64).optional(),
  tag: z.string().trim().max(WORD_RECORD_LIMITS.maxTagChars).optional(),
  articleId: z.string().trim().max(64).optional(),
  sort: z.enum(['recent', 'due', 'familiarity', 'alpha']).default('recent'),
});
export type WordbookQuery = z.infer<typeof wordbookQuerySchema>;

export const exportQuerySchema = z.object({
  format: z.enum(EXPORT_FORMATS).default('csv'),
  status: z.enum(WORD_STATUSES).optional(),
  /** 只导出到期的 */
  due: booleanQuery,
});
export type ExportQuery = z.infer<typeof exportQuerySchema>;

// ---------------------------------------------------------------------------
// 复习（阶段 4）
// ---------------------------------------------------------------------------

export const reviewQueueQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(50).default(20),
  /**
   * due：只取到期的（默认，尊重 SRS）；
   * mixed：到期卡不够时用"最不熟的新卡"补齐，让用户随时能练。
   */
  mode: z.enum(['due', 'mixed']).default('due'),
  /** 指定文章：复习"这篇里我标过的词" */
  articleId: z.string().trim().max(64).optional(),
});
export type ReviewQueueQuery = z.infer<typeof reviewQueueQuerySchema>;

export const reviewSubmitItemSchema = z.object({
  recordId: z.string().trim().min(1).max(64),
  /** 0 忘记 / 1 困难 / 2 模糊 / 3 记得 / 4 秒答 */
  rating: z.coerce.number().int().min(0).max(4),
  /** 本张卡停留时长（毫秒），用于统计，不参与调度 */
  elapsedMs: z.coerce.number().int().min(0).max(10 * 60 * 1000).optional(),
});
export type ReviewSubmitItemInput = z.infer<typeof reviewSubmitItemSchema>;

export const reviewSubmitSchema = z.object({
  /** 批量提交：一次会话结束时整批写入，避免每张卡一次往返 */
  items: z.array(reviewSubmitItemSchema).min(1, '至少提交一张卡片').max(100, '一次最多提交 100 张'),
});
export type ReviewSubmitInput = z.infer<typeof reviewSubmitSchema>;

/** 单卡重置（"这张我完全不记得，从头开始"）。 */
export const reviewResetSchema = z.object({
  recordId: z.string().trim().min(1).max(64),
});
export type ReviewResetInput = z.infer<typeof reviewResetSchema>;

export const SRS_STATE_VALUES = SRS_STATES;

// ---------------------------------------------------------------------------
// 长难句分析（阶段 5）
// ---------------------------------------------------------------------------

export const SENTENCE_TEXT_MAX = 600;

export const sentenceAnalyzeSchema = z
  .object({
    sentenceId: z.string().trim().min(1).max(64).optional(),
    articleId: z.string().trim().max(64).optional(),
    globalIndex: z.coerce.number().int().min(0).max(100_000).optional(),
    /** 裸文本（用户粘贴的句子）。与上面三种互斥，服务层优先用 id */
    text: z.string().trim().min(3, '句子太短，无法分析').max(SENTENCE_TEXT_MAX, '句子过长，请只提交一句话').optional(),
    /** 是否强制重新分析（忽略缓存），后台排查用 */
    force: z.boolean().optional(),
  })
  .refine((value) => Boolean(value.sentenceId ?? value.text ?? (value.articleId && value.globalIndex !== undefined)), {
    message: '请提供 sentenceId、articleId+globalIndex 或 text 三者之一',
    path: ['text'],
  });
export type SentenceAnalyzeInput = z.infer<typeof sentenceAnalyzeSchema>;

export const longSentenceListQuerySchema = pageQuerySchema.extend({
  articleId: z.string().trim().max(64).optional(),
  /** 是否只要未解析过的（后台补解析用） */
  unanalyzed: booleanQuery,
  /** 排序：score 难的在前，recent 新导入在前 */
  sort: z.enum(['score', 'recent']).default('score'),
});
export type LongSentenceListQuery = z.infer<typeof longSentenceListQuerySchema>;

// ---------------------------------------------------------------------------
// 水平评估（阶段 6）
// ---------------------------------------------------------------------------

export const levelAssessSchema = z.object({
  /** 针对单篇文章评估；不传表示基于全量学习历史 */
  articleId: z.string().trim().max(64).optional(),
  /** 是否强制重算（默认复用 5 分钟内的结果） */
  force: z.boolean().optional(),
});
export type LevelAssessInput = z.infer<typeof levelAssessSchema>;

export const levelReportQuerySchema = z.object({
  history: booleanQuery,
  limit: z.coerce.number().int().min(1).max(50).default(20),
});
export type LevelReportQuery = z.infer<typeof levelReportQuerySchema>;

// ---------------------------------------------------------------------------
// 学习建议（阶段 7）
// ---------------------------------------------------------------------------

export const learningAdviceQuerySchema = z.object({
  mode: z.enum(STUDY_MODES).optional(),
  /** 是否强制重新生成（忽略缓存） */
  force: booleanQuery,
});
export type LearningAdviceQuery = z.infer<typeof learningAdviceQuerySchema>;

// ---------------------------------------------------------------------------
// 埋点（阶段 3 起）
// ---------------------------------------------------------------------------

export const MAX_EVENT_BATCH = 30;

export const eventItemSchema = z.object({
  type: z.enum(EVENT_TYPES),
  articleId: z.string().trim().max(64).optional(),
  wordId: z.string().trim().max(64).optional(),
  /** 任意上下文；服务层会截断到 2 KB */
  payload: z.unknown().optional(),
  anonId: z.string().trim().max(64).optional(),
  sessionId: z.string().trim().max(64).optional(),
});
export type EventItemInput = z.infer<typeof eventItemSchema>;

/** 支持单个对象或数组 —— 前端有时只报一个事件，没必要包一层数组。 */
export const eventsSubmitSchema = z.union([
  eventItemSchema,
  z.object({ events: z.array(eventItemSchema).min(1).max(MAX_EVENT_BATCH) }),
]);
export type EventsSubmitInput = z.infer<typeof eventsSubmitSchema>;

// ---------------------------------------------------------------------------
// 账号与设置（阶段 7 的隐私入口）
// ---------------------------------------------------------------------------

/** 用户设置的 patch。只允许改这几个字段，`theme` 与阅读器参数都在这里。 */
export const userSettingsSchema = z.object({
  theme: z.enum(['light', 'dark', 'system']).optional(),
  readerFontSize: z.coerce.number().int().min(14).max(28).optional(),
  readerLineHeight: z.coerce.number().min(1.2).max(2.6).optional(),
  defaultStudyMode: z.enum(STUDY_MODES).optional(),
});
export type UserSettingsInput = z.infer<typeof userSettingsSchema>;

/**
 * 注销账号。
 *
 * `confirm` 必须是字面量 true：这是**不可撤销**的操作，必须由界面显式勾选，
 * 就像导入文章时的授权确认一样 —— 缺了它返回 400 而不是静默执行。
 *
 * `mode` 的两种语义（详见 README 的隐私说明）：
 * - `soft`：停用账号并撤销全部登录态，数据保留（可由管理员恢复）；
 * - `purge`：**真正删除**个人数据（生词本、复习日志、阅读进度、评估记录、会话），
 *   级联删除由数据库外键保证；用户导入的文章会保留但脱离归属（其他读者还在读）。
 */
export const accountDeleteSchema = z.object({
  mode: z.enum(['soft', 'purge']).default('soft'),
  confirm: z.literal(true, { errorMap: () => ({ message: '请先确认你要注销账号' }) }),
});
export type AccountDeleteInput = z.infer<typeof accountDeleteSchema>;

// ---------------------------------------------------------------------------
// 管理后台（阶段 7）
// ---------------------------------------------------------------------------

export const aiCacheQuerySchema = pageQuerySchema.extend({
  namespace: z.string().trim().max(40).optional(),
  q: z.string().trim().max(100).optional(),
  status: z.enum(['ok', 'error']).optional(),
  /** 只列命名空间里最近 N 天 */
  days: z.coerce.number().int().min(1).max(365).optional(),
});
export type AiCacheQuery = z.infer<typeof aiCacheQuerySchema>;

export const adminUserQuerySchema = pageQuerySchema.extend({
  q: z.string().trim().max(100).optional(),
  role: z.enum(['student', 'teacher', 'admin']).optional(),
  status: z.enum(['active', 'disabled', 'deleted']).optional(),
});
export type AdminUserQuery = z.infer<typeof adminUserQuerySchema>;

export const adminUserUpdateSchema = z.object({
  role: z.enum(['student', 'teacher', 'admin']).optional(),
  status: z.enum(['active', 'disabled', 'deleted']).optional(),
});
export type AdminUserUpdateInput = z.infer<typeof adminUserUpdateSchema>;
